import { Link, router } from "@inertiajs/react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/StatusBadge"
import { timeAgo } from "@/lib/date"
import { RefreshCw, Trash2 } from "lucide-react"
import { useState } from "react"
import type { Capture } from "@/types"

interface CapturesTableProps {
  captures: Capture[]
  showSource?: boolean
}

export function CapturesTable({ captures, showSource = false }: CapturesTableProps) {
  const [deleteId, setDeleteId] = useState<number | null>(null)

  if (captures.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No captures yet.
      </p>
    )
  }

  function handleResend(e: React.MouseEvent, captureId: number) {
    e.stopPropagation()
    router.post(`/admin/captures/${captureId}/retry_delivery`, {}, { preserveScroll: true })
  }

  function handleDelete() {
    if (deleteId === null) return
    router.delete(`/admin/captures/${deleteId}`, { preserveScroll: true })
    setDeleteId(null)
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            {showSource && <TableHead>Source</TableHead>}
            <TableHead>Status</TableHead>
            <TableHead>Captured At</TableHead>
            <TableHead>Delivered At</TableHead>
            <TableHead className="w-[80px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {captures.map((capture) => (
            <TableRow
              key={capture.id}
              className="cursor-pointer"
              onClick={() => router.visit(`/admin/captures/${capture.id}`)}
            >
              <TableCell className="max-w-[300px] truncate font-medium">
                {capture.title || "Untitled"}
              </TableCell>
              {showSource && (
                <TableCell>
                  {capture.source_name ? (
                    <Link
                      href={`/admin/sources/${capture.source_id}`}
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {capture.source_name}
                    </Link>
                  ) : (
                    "-"
                  )}
                </TableCell>
              )}
              <TableCell>
                <StatusBadge status={capture.status} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {timeAgo(capture.captured_at)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {capture.delivered_at ? timeAgo(capture.delivered_at) : "-"}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors"
                    title={capture.status === "delivered" ? "Re-deliver" : "Retry delivery"}
                    onClick={(e) => handleResend(e, capture.id)}
                  >
                    <RefreshCw className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive rounded p-1 transition-colors"
                    title="Delete capture"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteId(capture.id)
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Capture</DialogTitle>
            <DialogDescription>
              Are you sure? This will also cancel any pending delivery jobs for this capture.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
